import { AsyncStatus, PhoneNumber } from 'lib-shared-legacy-types';
import { SearchUserHit, User } from 'lib-shared-types-api';
import _findIndex from 'lodash/findIndex';
import { handleActions } from 'redux-actions';

import { userModalActions } from 'components/usersModal';

import { twilioActions } from '../../../app/middleware/twilio/actions/twilioActions';
import * as conversationActions from '../../conversation/actions/conversation';
import { actions } from '../actions/telephony';

const {
  conversationCloseSucceeded,
  conversationWsClose,
  conversationOutboundStarted,
} = conversationActions.actions;

const {
  twilioEstablishedCall,
  twilioDisconnectedCall,
  twilioChangeDeviceStatus,
} = twilioActions;

export const {
  telephonyGetUsersByQuery,
  telephonyGetUsersByQuerySucceeded,
  telephonyGetUsersByQueryFailed,
  telephonyGetAllCountryCodes,
  telephonyGetAllCountryCodesSucceeded,
  telephonyGetAllCountryCodesFailed,
  telephonyResetUsers,
  telephonyRenewToken,
  telephonyRenewTokenSucceeded,
  telephonyRenewTokenFailed,
  telephonyGetAllOwnNumbers,
  telephonyGetAllOwnNumbersSucceeded,
  telephonyGetAllOwnNumbersFailed,
  telephonyGetDefaultNumber,
  telephonyGetDefaultNumberSucceeded,
  telephonyGetDefaultNumberFailed,
  telephonySetBlocked,
  telephonyOpenDropdown,
  telephonyCloseDropdown,
  telephonySetHold,
  telephonySetHoldSucceeded,
  telephonySetHoldFailed,
  telephonySetMute,
  telephonySetMuteSucceeded,
  telephonySetMuteFailed,
  telephonyChangeFromNumber,
  telephonyGetQueryNumbers,
  telephonyGetQueryNumbersSucceeded,
  telephonyGetQueryNumbersFailed,
  telephonySetSearchValue,
  telephonyResetHold,
} = actions;

export type TelephonyState = {
  meta: {
    asyncStatus: AsyncStatus;
    usersAsyncStatus: AsyncStatus;
    dynamicTelephoneNumbersArrayAsyncStatus: AsyncStatus;
    staticTelephoneNumbersArrayAsyncStatus: AsyncStatus;
    allCountryCodesAsyncStatus: AsyncStatus;
    defaultNumberAsyncStatus: AsyncStatus;
    mute: AsyncStatus;
    hold: AsyncStatus;
  };
  ui: {
    isTelephonyDropdownOpen: boolean;
  };
  selectedFromNumber: PhoneNumber | null;
  token: string | null;
  isOnHold: boolean;
  isOnMute: boolean;
  isReady: boolean;
  searchValue: string;
  isMicrophoneBlocked: boolean;
  users: Array<SearchUserHit>;
  dynamicTelephoneNumbersArray: Array<PhoneNumber>;
  staticTelephoneNumbersArray: Array<PhoneNumber>;
  allCountryCodes: Array<any>;
  currentCall: {
    csid?: number;
    enduser?: User;
  } | null;
  previousPage: string | null;
  nextPage: string | null;
};

export const initialState: TelephonyState = {
  meta: {
    asyncStatus: 'succeeded',
    usersAsyncStatus: 'succeeded',
    dynamicTelephoneNumbersArrayAsyncStatus: 'succeeded',
    staticTelephoneNumbersArrayAsyncStatus: 'succeeded',
    allCountryCodesAsyncStatus: 'succeeded',
    defaultNumberAsyncStatus: 'succeeded',
    mute: 'succeeded',
    hold: 'succeeded',
  },
  ui: {
    isTelephonyDropdownOpen: false,
  },
  selectedFromNumber: null,
  token: null,
  isOnHold: false,
  isOnMute: false,
  isReady: true, // it's true because we first try to make an twilio instance when making the first call.
  searchValue: '',
  isMicrophoneBlocked: false,
  users: [],
  dynamicTelephoneNumbersArray: [],
  staticTelephoneNumbersArray: [],
  allCountryCodes: [],
  currentCall: null,
  previousPage: null,
  nextPage: null,
};

const telephony = handleActions<TelephonyState, any, any>(
  {
    [telephonyResetHold.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, mute: 'succeeded', hold: 'succeeded' },
        isOnHold: false,
      };
    },

    [telephonyChangeFromNumber.toString()]: (state, action) => {
      return { ...state, selectedFromNumber: action.payload.number };
    },

    [telephonyResetUsers.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, usersAsyncStatus: 'succeeded' },
        users: [],
      };
    },

    [telephonyGetAllCountryCodes.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, allCountryCodesAsyncStatus: 'loading' },
      };
    },

    [telephonySetSearchValue.toString()]: (state, action) => {
      return {
        ...state,
        searchValue: action.payload !== undefined ? action.payload : '',
      };
    },

    [telephonyGetAllCountryCodesSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, allCountryCodesAsyncStatus: 'succeeded' },
        allCountryCodes: action.payload.data.countries,
      };
    },

    [telephonyGetAllCountryCodesFailed.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, allCountryCodesAsyncStatus: 'failed' },
      };
    },

    [telephonySetHold.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, hold: 'loading' } };
    },

    [telephonySetHoldSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        isOnHold: action.payload.meta.request,
        meta: { ...state.meta, hold: 'succeeded' },
      };
    },

    [telephonySetHoldFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, hold: 'failed' } };
    },

    [telephonySetMute.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, mute: 'loading' } };
    },

    [telephonySetMuteSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, mute: 'succeeded' },
        isOnMute: action.payload.meta.request,
      };
    },

    [telephonySetMuteFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, mute: 'failed' } };
    },

    [telephonyGetQueryNumbers.toString()]: (state) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          dynamicTelephoneNumbersArrayAsyncStatus: 'loading',
        },
      };
    },

    [telephonyGetQueryNumbersSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          dynamicTelephoneNumbersArrayAsyncStatus: 'succeeded',
        },
        dynamicTelephoneNumbersArray:
          action.payload.data &&
          Array.isArray(action.payload.data.numbers) === true
            ? action.payload.data.numbers
            : [],
        nextPage: action.payload.meta ? action.payload.meta.next_page : null,
        previousPage: action.payload.meta
          ? action.payload.meta.prev_page
          : null,
      };
    },

    [telephonyGetQueryNumbersFailed.toString()]: (state) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          dynamicTelephoneNumbersArrayAsyncStatus: 'failed',
        },
      };
    },

    [telephonyGetAllOwnNumbers.toString()]: (state) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          staticTelephoneNumbersArrayAsyncStatus: 'loading',
        },
      };
    },

    [telephonyGetAllOwnNumbersSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          staticTelephoneNumbersArrayAsyncStatus: 'succeeded',
        },
        staticTelephoneNumbersArray:
          action.payload.data &&
          Array.isArray(action.payload.data.numbers) === true
            ? action.payload.data.numbers
            : [],
      };
    },

    [telephonyGetAllOwnNumbersFailed.toString()]: (state) => {
      return {
        ...state,
        meta: {
          ...state.meta,
          staticTelephoneNumbersArrayAsyncStatus: 'failed',
        },
      };
    },

    [telephonyGetDefaultNumber.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, defaultNumberAsyncStatus: 'loading' },
      };
    },

    [telephonyGetDefaultNumberSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, defaultNumberAsyncStatus: 'succeeded' },
        selectedFromNumber: action.payload.data?.e164_format
          ? action.payload.data
          : null,
      };
    },

    [telephonyGetDefaultNumberFailed.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, defaultNumberAsyncStatus: 'failed' },
      };
    },

    [telephonyGetUsersByQuery.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, usersAsyncStatus: 'loading' } };
    },

    ['TELEPHONY_BUY_NUMBER_SUCCEEDED']: (state, action) => {
      const newNumber = action.payload.result?.data
        ? Object.assign({}, action.payload.result.data)
        : null;
      if (newNumber) {
        return {
          ...state,
          staticTelephoneNumbersArray: [
            ...state.staticTelephoneNumbersArray,
            newNumber,
          ],
        };
      }
      return state;
    },

    [telephonyGetUsersByQuerySucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, usersAsyncStatus: 'succeeded' },
        users:
          Array.isArray(action.payload.data) === true &&
          action.payload.data[0] !== undefined &&
          action.payload.data[0].hits !== undefined
            ? action.payload.data[0].hits
            : [],
      };
    },

    [telephonyGetUsersByQueryFailed.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, usersAsyncStatus: 'failed' } };
    },

    [telephonyRenewToken.toString()]: (state) => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [telephonyRenewTokenSucceeded.toString()]: (state, action) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        token: action.payload.data.token,
      };
    },

    [telephonyRenewTokenFailed.toString()]: (state) => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'failed' },
        token: null,
      };
    },

    [telephonyOpenDropdown.toString()]: (state) => {
      return { ...state, ui: { ...state.ui, isTelephonyDropdownOpen: true } };
    },

    [telephonyCloseDropdown.toString()]: (state) => {
      return { ...state, ui: { ...state.ui, isTelephonyDropdownOpen: false } };
    },

    // USER MODAL ACTIONS.
    [userModalActions.user.update.pending.type]: (state) => {
      return { ...state, meta: { ...state.meta, usersAsyncStatus: 'loading' } };
    },

    // We need to update based on userModal action.
    [userModalActions.user.update.fulfilled.type]: (state, action) => {
      const {
        payload: { data: updatedUser },
      } = action as ReturnType<typeof userModalActions.user.update.fulfilled>;
      const users = state.users.slice();
      const index = _findIndex(users, { id: updatedUser.id });

      if (index > -1) {
        Object.assign(users[index], {
          additional_emails: updatedUser.additional_emails ?? [],
          additional_phone_numbers: updatedUser.additional_phone_numbers ?? [],
          avatar_url: updatedUser.avatar_url ?? undefined,
          company_id: updatedUser.company_id ?? undefined,
          display_name: updatedUser.name ?? undefined,
          email: updatedUser.email ?? undefined,
          phone_number: updatedUser.phone_number ?? undefined,
          user_type: updatedUser.type,
        } satisfies Partial<SearchUserHit>);
      }
      return {
        ...state,
        users,
        meta: { ...state.meta, usersAsyncStatus: 'succeeded' },
      };
    },

    // We need to update based on userModal action.
    [userModalActions.user.update.rejected.type]: (state) => {
      return { ...state, meta: { ...state.meta, usersAsyncStatus: 'failed' } };
    },

    [telephonySetBlocked.toString()]: (state) => {
      return { ...state, isMicrophoneBlocked: true };
    },

    [twilioChangeDeviceStatus.toString()]: (state, action) => {
      console.warn('action', action.payload);
      return {
        ...state,
        isReady:
          action.payload._status === 'ready' ||
          action.payload.state === 'registered',
      };
    },

    [twilioDisconnectedCall.toString()]: (state, action) => {
      let currentCall = state.currentCall;
      let isOnMute = state.isOnMute;
      let isOnHold = state.isOnHold;

      const { conference } = action.payload.message;
      if (
        conference &&
        currentCall &&
        conference.toString() === currentCall.csid?.toString()
      ) {
        currentCall = null;
        isOnMute = false;
        isOnHold = false;
      }

      return {
        ...state,
        currentCall: currentCall,
        isOnHold: isOnHold,
        isOnMute: isOnMute,
      };
    },

    [twilioEstablishedCall.toString()]: (state, action) => {
      let currentCall = state.currentCall;
      let isOnMute = state.isOnMute;
      const { csid, muted } = action.payload;

      if (csid) {
        if (muted === 'true') {
          isOnMute = true;
        }
        currentCall = {
          csid: csid,
        };
      }

      return { ...state, currentCall: currentCall, isOnMute: isOnMute };
    },

    [conversationOutboundStarted.toString()]: (state, action) => {
      let currentCall = state.currentCall;
      const { csid, channel, enduser } = action.payload;
      if (channel === 'pstnphone') {
        currentCall = {
          csid: csid,
          enduser: enduser,
        };
      }
      return { ...state, currentCall: currentCall };
    },

    [conversationWsClose.toString()]: (state, action) => {
      let currentCall = state.currentCall;
      let isOnMute = state.isOnMute;
      let isOnHold = state.isOnHold;

      // Close if the call is conference and we know this csid in telephony.
      if (
        currentCall?.csid &&
        currentCall.csid === action.payload.params.csid
      ) {
        currentCall = null;
        isOnMute = false;
        isOnHold = false;
      }
      return {
        ...state,
        currentCall: currentCall,
        isOnMute: isOnMute,
        isOnHold: isOnHold,
      };
    },

    [conversationCloseSucceeded.toString()]: (state, action) => {
      let currentCall = state.currentCall;
      let isOnMute = state.isOnMute;
      let isOnHold = state.isOnHold;

      // Close if the call is conference and we know this csid in telephony.
      if (currentCall?.csid && currentCall.csid === action.payload.data.id) {
        currentCall = null;
        isOnMute = false;
        isOnHold = false;
      }
      return {
        ...state,
        currentCall: currentCall,
        isOnMute: isOnMute,
        isOnHold: isOnHold,
      };
    },
  },
  initialState,
);

export default telephony;
